/* eslint-disable object-shorthand */
/* eslint-disable func-names */
/* eslint-disable react/no-unstable-nested-components */
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import getHairlineColor from "components/charts/modern/overlay/getHairlineColor";
import UnlockPremiumContentCTA from "components/conversion/UnlockPremiumContentCTA";
import Avatar from "components/core/Avatar";
import FullBleedButton from "components/financials/modern/FullBleedButton";
import SelectIndicatorsDataSource from "components/financials/modern/SelectIndicatorsDataSource";
import SelectNumberOfRows from "components/financials/modern/SelectNumberOfRows";
import SelectStatementType from "components/financials/modern/SelectStatementType";
import useFinancialsSheetForComparison from "components/financials/modern/data/useFinancialsSheetForComparison";
import getCompanyIssuerLinkProps from "components/financials/modern/getCompanyIssuerLinkProps";
import type { IFinancialsSheetForComparisonQuery } from "components/financials/modern/graphql/__generated__/FinancialsSheetForComparisonQuery.generated";
import type { IIndicatorFieldsFragment } from "components/financials/modern/graphql/fragments/__generated__/IndicatorFieldsFragment.generated";
import IndicatorDocumentationPopover from "components/financials/modern/indicators/IndicatorDocumentationPopover";
import IndicatorsButtonArray from "components/financials/modern/indicators/IndicatorsButtonArray";
import SelectArbitraryIndicatorDebouncer from "components/financials/modern/indicators/SelectArbitraryIndicatorDebouncer";
import SelectArbitraryIndicatorPopover from "components/financials/modern/indicators/SelectArbitraryIndicatorPopover";
import { overrideVerticalForDataSource } from "components/financials/modern/utils/platformVertical";
import type { SelectedCompanyIssuer } from "components/issuers/IssuerByTickerSymbolSearch";
import IssuerByTickerSymbolSearch, {
  IssuerByTickerSymbolSearchInputTrigger,
  SelectedIssuersStack,
} from "components/issuers/IssuerByTickerSymbolSearch";
import RelatedPeersComboOfferButton from "components/issuers/RelatedPeersComboOfferButton";
import LoadingSkeleton from "components/pages/TemplatesGalleryPage/LoadingSkeleton";
import { PopoverTrigger } from "components/ui/Popover";
import { ScrollArea } from "components/ui/ScrollArea";
import { SimpleTextTooltip } from "components/ui/Tooltip";
import classNames from "components/ui/classNames";
import useTheme from "components/ui/useTheme";
import ShareThisPageDialog from "components/ux/ShareThisPageDialog";
import { parseISO } from "date-fns";
import getYear from "date-fns/getYear";
import lightFormat from "date-fns/lightFormat";
import useSelfie from "lib/hooks/useSelfie";
import notUndefinedOrNull from "lib/utils/notUndefinedOrNull";
import sortArray from "lib/utils/sortArray";
import uniqBy from "lodash/uniqBy";
import { InfoIcon, LandmarkIcon, XIcon } from "lucide-react";
import Link from "next/link";
import { invariant } from "outvariant";
import { FormattedMessage, useIntl } from "react-intl";
import colors from "tailwindcss/colors";
import tinycolor from "tinycolor2";
import type { IIndicatorDataSource } from "types/graphqlTypes";
import {
  IFinancialsSheetLimitCriterion,
  IFinancialsStatementType,
  IPlatformVertical,
  ISecurityCountry,
} from "types/graphqlTypes";

interface FinancialsComparisonProps {
  vertical: IPlatformVertical;
  indicatorDataSource: IIndicatorDataSource;
  statementType?: IFinancialsStatementType;
  interim?: string | null;
  tickerSymbol?: string;
  country?: ISecurityCountry;
  currency?: string;
  limit?: number;
  sortDirection?: "ASC" | "DESC";
  selectedIssuers?: SelectedCompanyIssuer[];
  indicators?: IIndicatorFieldsFragment[];
  arbitraryIndicators?: IIndicatorFieldsFragment[];

  canChangeDataSource?: boolean;

  onChangeIndicators?: (indicators: IIndicatorFieldsFragment[]) => void;
  onChangeLimit?: (limit: number) => void;
  onChangeIndicatorDataSource?: (dataSource: IIndicatorDataSource) => void;
  onChangeAdversaries?: (adversaries: SelectedCompanyIssuer[]) => void;
  onChangeStatementType?: (statementType: IFinancialsStatementType) => void;
  onChangeInterim?: (interim: string | null) => void;
}

export default function FinancialsComparison({
  vertical,
  interim,
  selectedIssuers = [],
  indicators: _indicators = [],
  arbitraryIndicators = [],
  statementType = IFinancialsStatementType.Annual,
  tickerSymbol,
  country,
  indicatorDataSource,
  limit = 5,
  sortDirection = "ASC",
  currency = "USD",
  canChangeDataSource = false,
  onChangeIndicatorDataSource,
  onChangeIndicators,
  onChangeAdversaries,
  onChangeStatementType,
  onChangeInterim,
  onChangeLimit,
}: FinancialsComparisonProps) {
  const viewer = useSelfie();

  const adversariesForComparison = [
    tickerSymbol && country
      ? {
          tickerSymbol,
          country,
        }
      : null,
    ...selectedIssuers.map((node) => {
      return {
        tickerSymbol: node.tickerSymbol,
        country: node.exchangeId === "BVMF" ? ISecurityCountry.Br : ISecurityCountry.Us,
      };
    }),
  ]
    .filter(notUndefinedOrNull)
    .slice(0, 5);

  const {
    loading,
    reportDates: _reportDates,
    indicators,
    adversaries: _adversaries,
    renderingTemplate,
  } = useFinancialsSheetForComparison({
    indicators: adversariesForComparison.length > 0 ? _indicators.map(({ objectId }) => objectId) : [],
    indicatorDataSource,
    statementType,
    currency,
    vertical: overrideVerticalForDataSource(vertical, indicatorDataSource),
    adversaries: adversariesForComparison,
    limit,
    limitCriterion: IFinancialsSheetLimitCriterion.NumberOfRows,
    filterByInterim: interim || null,
    skip: false,
  });

  const rows = sortArray(
    _reportDates.map((reportDate, index) => {
      return {
        index,
        reportDate: parseISO(reportDate),
      };
    }),
    "DESC",
    sortDirection,
  );

  const adversaries = _adversaries.map((adversary) => {
    const reportDates = _reportDates.map((_reportDate, index) => {
      return adversary.reportDates[index] ? adversary.reportDates[index] : null;
    });
    const isAnnual = _reportDates.map((_reportDate, index) => {
      return adversary.isAnnual[index] ? adversary.isAnnual[index] : null;
    });
    const isQuarterly = _reportDates.map((_reportDate, index) => {
      return adversary.isQuarterly[index] ? adversary.isQuarterly[index] : null;
    });
    const isTrailingTwelveMonths = _reportDates.map((_reportDate, index) => {
      return adversary.isTrailingTwelveMonths[index] ? adversary.isTrailingTwelveMonths[index] : null;
    });
    const isPreliminaryTrailingTwelveMonths = _reportDates.map((_reportDate, index) => {
      return adversary.isPreliminaryTrailingTwelveMonths[index]
        ? adversary.isPreliminaryTrailingTwelveMonths[index]
        : null;
    });
    return {
      ...adversary,
      reportDates: sortArray(reportDates, "DESC", sortDirection),
      isAnnual: sortArray(isAnnual, "DESC", sortDirection),
      isQuarterly: sortArray(isQuarterly, "DESC", sortDirection),
      isTrailingTwelveMonths: sortArray(isTrailingTwelveMonths, "DESC", sortDirection),
      isPreliminaryTrailingTwelveMonths: sortArray(isPreliminaryTrailingTwelveMonths, "DESC", sortDirection),
    };
  });

  return (
    <div className="h-full w-full space-y-4">
      <div className={classNames("grid grid-cols-1 items-start gap-4 xl:grid-cols-[_0.8fr,_0.5fr,_1fr]")}>
        <div className="flex items-center gap-x-4 lg:hidden">
          <IssuerByTickerSymbolSearch
            selectedIssuers={selectedIssuers}
            tickerSymbol={tickerSymbol}
            vertical={vertical}
            disabled={selectedIssuers.length >= 5}
            onChange={(issuer) => {
              onChangeAdversaries?.([...selectedIssuers, issuer]);
            }}
          >
            <IssuerByTickerSymbolSearchInputTrigger disabled={loading} className={classNames("lg:w-full")} />
          </IssuerByTickerSymbolSearch>
        </div>

        <div className="grid grid-cols-2 items-center gap-4 lg:flex">
          <div className="col-span-2">
            <SelectArbitraryIndicatorDebouncer
              selectedIndicators={indicators}
              onToggleIndicator={(indicator, isSelected) => {
                const selectedIndicators = isSelected
                  ? indicators.filter(({ objectId }) => objectId !== indicator.objectId)
                  : [...indicators, indicator];
                onChangeIndicators?.(selectedIndicators);
              }}
            >
              {({ selectedIndicators, onToggleIndicator }) => (
                <SelectArbitraryIndicatorPopover
                  vertical={vertical}
                  indicatorDataSource={indicatorDataSource}
                  mode="TOGGLE"
                  limit={3}
                  selectedIndicators={selectedIndicators}
                  onToggleIndicator={onToggleIndicator}
                />
              )}
            </SelectArbitraryIndicatorDebouncer>
          </div>

          {vertical !== IPlatformVertical.Fiis && (
            <SelectStatementType
              disabled={loading}
              statementType={statementType}
              onChangeStatementType={onChangeStatementType}
              onChangeInterim={onChangeInterim}
            />
          )}

          <div className="inline-flex lg:hidden">
            <SelectNumberOfRows value={limit} onChange={(limit) => onChangeLimit?.(limit)} />
          </div>
        </div>

        <div className="hidden items-center gap-x-4 lg:flex">
          <IssuerByTickerSymbolSearch
            selectedIssuers={selectedIssuers}
            tickerSymbol={tickerSymbol}
            vertical={vertical}
            disabled={selectedIssuers.length >= 5}
            onChange={(issuer) => {
              onChangeAdversaries?.([...selectedIssuers, issuer]);
            }}
          >
            <IssuerByTickerSymbolSearchInputTrigger disabled={loading} className={classNames("lg:w-full")} />
          </IssuerByTickerSymbolSearch>
        </div>

        <div className="flex flex-col justify-start gap-4 sm:flex-row lg:flex xl:justify-end">
          {indicators.length > 0 && adversariesForComparison.length > 0 && (
            <div className="inline-flex">
              <RelatedPeersComboOfferButton
                limit={5}
                vertical={vertical}
                tickerSymbol={adversariesForComparison[0].tickerSymbol}
                selectedIssuers={selectedIssuers}
                onClick={(combo) => {
                  if (tickerSymbol && country) {
                    onChangeAdversaries?.(combo);
                    return;
                  }
                  onChangeAdversaries?.([...selectedIssuers, ...combo]);
                }}
              />
            </div>
          )}

          <SelectedIssuersStack
            selectedIssuers={selectedIssuers}
            onChange={(next) => {
              onChangeAdversaries?.(next);
            }}
          />

          {canChangeDataSource && (
            <SelectIndicatorsDataSource
              value={indicatorDataSource}
              onChange={(nextDataSource) => {
                onChangeIndicatorDataSource?.(nextDataSource);
                onChangeIndicators?.([]);
              }}
            />
          )}

          <div className="hidden lg:inline-flex">
            <SelectNumberOfRows value={limit} onChange={(limit) => onChangeLimit?.(limit)} />
          </div>

          {viewer && viewer.isPremium && <FullBleedButton className="hidden sm:flex" />}

          <ShareThisPageDialog />
        </div>
      </div>

      {renderingTemplate && renderingTemplate.indicators.length > 0 && (
        <IndicatorsButtonArray
          // className="-mx-3"
          selectedIndicators={indicators}
          indicators={uniqBy(
            [
              ...indicators,
              ...arbitraryIndicators.filter(
                (node) => !renderingTemplate.indicators.find((indicator) => node.objectId === indicator.objectId),
              ),
              ...renderingTemplate.indicators,
            ],
            "objectId",
          )}
          onToggleIndicator={(indicator, isSelected) => {
            const selectedIndicators = isSelected
              ? indicators.filter(({ objectId }) => objectId !== indicator.objectId)
              : [...(indicators.length === 3 ? indicators.slice(0, 2) : indicators), indicator];

            onChangeIndicators?.(selectedIndicators);
          }}
        />
      )}

      {/* There's adversaries, but no indicators */}
      {indicators.length === 0 && adversariesForComparison.length > 0 && (
        <div
          className={classNames(
            "rounded-xl bg-amber-500/5 p-4 text-center text-sm tracking-wide text-amber-600",
            "space-y-4",
          )}
        >
          <InfoIcon className="inline-flex h-6 w-6" />
          <p className="text-center font-medium">
            <FormattedMessage defaultMessage="Selecione um indicador para montar um comparativo..." id="6Mpfvf" />
          </p>
        </div>
      )}

      {indicators.length === 0 && adversariesForComparison.length === 0 && (
        <div
          className={classNames(
            "rounded-xl bg-teal-500/5 p-4 text-center text-sm tracking-wide text-teal-600",
            "space-y-4",
          )}
        >
          <LandmarkIcon className="inline-flex h-6 w-6" />
          <p className="text-center font-medium">
            <FormattedMessage defaultMessage="Pesquise um ativo para montar um comparativo..." id="+WKRz2" />
          </p>
        </div>
      )}

      {/* There's no adversaries  */}
      {selectedIssuers.length === 0 && false && (
        <div>
          <IssuerByTickerSymbolSearch
            selectedIssuers={selectedIssuers}
            tickerSymbol={tickerSymbol}
            vertical={vertical}
            disabled={adversaries.length >= 5}
            onChange={(issuer) => {
              onChangeAdversaries?.([...selectedIssuers, issuer]);
            }}
          >
            <IssuerByTickerSymbolSearchInputTrigger disabled={loading} size="big" />
          </IssuerByTickerSymbolSearch>
        </div>
      )}

      {loading && selectedIssuers.length > 0 && indicators.length > 0 && adversaries.length === 0 ? (
        <LoadingSkeleton count={1} className="h-56" />
      ) : (
        <>
          <FinancialsComparisonTable
            loading={loading}
            statementType={statementType}
            indicators={indicators}
            rows={rows}
            adversaries={adversaries}
            selectedIssuers={selectedIssuers}
            onChangeSelectedIssuers={(selectedIssuers) => {
              onChangeAdversaries?.([...selectedIssuers]);
            }}
          />

          {(!viewer?.isPremium || viewer.isTrialing) && indicators.length > 0 && (
            <UnlockPremiumContentCTA origin="Financials Comparison" />
          )}
        </>
      )}
    </div>
  );
}

type FinancialsSheetForComparisonQuery = IFinancialsSheetForComparisonQuery["financialsSheetForComparison"];
interface FinancialsComparisonTableProps {
  loading: boolean;
  statementType: IFinancialsStatementType;
  indicators: FinancialsSheetForComparisonQuery["indicators"];
  adversaries: FinancialsSheetForComparisonQuery["adversaries"];
  selectedIssuers: SelectedCompanyIssuer[];
  onChangeSelectedIssuers?: (adversaries: SelectedCompanyIssuer[]) => void;
  rows: Array<{
    index: number;
    reportDate: Date;
  }>;
}

function useComparisonColumns({
  loading,
  rows,
  statementType,
  indicators,
  adversaries,
  selectedIssuers,
  onChangeSelectedIssuers,
}: FinancialsComparisonTableProps) {
  const intl = useIntl();
  const localeLanguageCode = intl.locale === "pt-BR" ? "pt" : "en";
  const theme = useTheme();
  const columnHelper = createColumnHelper<any>();

  return adversaries.map((adversary) => {
    return {
      id: adversary.tickerSymbol,
      header: () => {
        if (!adversary.issuer) {
          return (
            <div
              className={classNames(
                "flex flex-col items-center gap-2 bg-slate-50/50 p-4",
                "group-hover:bg-indigo-50/50 dark:group-hover:bg-slate-950/30",
                "dark:bg-slate-950",
              )}
            >
              <span
                className={classNames(
                  "rounded-md px-2 py-1 text-xs font-bold tracking-wider",
                  "bg-violet-50 text-violet-600",
                  "dark:bg-orange-300/30 dark:text-orange-100",
                  "inline-flex",
                )}
              >
                {adversary.tickerSymbol.toUpperCase()}
              </span>
            </div>
          );
        }

        const canBeRemoved = selectedIssuers.some((candidate) => {
          return candidate.tickerSymbol === adversary.tickerSymbol;
        });
        const hairlineColor = getHairlineColor(adversary.issuer, true);

        return (
          <div
            style={{
              backgroundColor:
                hairlineColor && theme === "dark" ? tinycolor(hairlineColor).setAlpha(0.1).toRgbString() : undefined,
            }}
            className={classNames(
              "flex items-center justify-between gap-2 bg-slate-50/50 p-4",
              "group-hover:bg-indigo-50/50 dark:group-hover:bg-slate-950/30",
              "dark:bg-slate-950",
            )}
          >
            <Link
              {...getCompanyIssuerLinkProps(adversary.issuer)}
              className="group flex items-center gap-4 rounded-2xl px-2 py-2 text-left hover:bg-slate-100 dark:hover:bg-slate-600/50"
              target="_blank"
            >
              <SimpleTextTooltip content={adversary.issuer.name}>
                <Avatar
                  src={adversary.issuer.niceLogo?.url || null}
                  hashValue={adversary.issuer._id}
                  alt={adversary.issuer.name}
                  size={36}
                  squircle={true}
                  className={classNames("md:inline-flex")}
                />
              </SimpleTextTooltip>
              <div className="space-y-1">
                <h1 className="max-w-28 truncate text-base text-slate-700 dark:text-white">{adversary.issuer.name}</h1>
                <span
                  className={classNames(
                    "rounded-md px-1.5 py-0.5 text-xs font-bold tracking-wider",
                    "bg-violet-50 text-violet-600",
                    "dark:bg-orange-300/30 dark:text-orange-100",
                    "inline-flex",
                  )}
                >
                  {adversary.tickerSymbol.toUpperCase()}
                </span>
              </div>
            </Link>
            {onChangeSelectedIssuers && canBeRemoved && (
              <SimpleTextTooltip content={intl.formatMessage({ defaultMessage: "Remover", id: "TolqfU" })}>
                <button
                  type="button"
                  className={classNames(
                    "rounded-full bg-red-50 p-1.5 text-xs text-red-600",
                    "hover:bg-red-600 hover:text-white",
                    "dark:bg-red-600/20 dark:text-red-300",
                  )}
                  onClick={(event) => {
                    const next = selectedIssuers.filter((node) => {
                      return node.tickerSymbol !== adversary.tickerSymbol;
                    });
                    event.stopPropagation();
                    onChangeSelectedIssuers?.(next);
                  }}
                >
                  <XIcon className="size-4" absoluteStrokeWidth={true} strokeWidth={2.5} />
                </button>
              </SimpleTextTooltip>
            )}
          </div>
        );
      },

      columns: [
        columnHelper.accessor("reportDate", {
          id: [adversary.tickerSymbol, "reportDate"].join("/"),
          meta: {
            header: {
              className: classNames("w-24"),
            },
            cell: {
              className: classNames(),
            },
          },
          header: () => (
            <div
              className={classNames(
                "w-24 bg-slate-50 px-2 py-1.5 text-sm font-semibold text-slate-700",
                "dark:bg-slate-800 dark:text-slate-300",
              )}
            >
              <FormattedMessage defaultMessage="Ano" id="jbWMDl" description="Ano fiscal dos balanços" />
            </div>
          ),
          cell: (info) => {
            const _reportDate = adversary.reportDates[info.row.index];

            const isTTM = adversary.isTrailingTwelveMonths[info.row.index];
            const isPTTM = adversary.isPreliminaryTrailingTwelveMonths[info.row.index];

            const isOdd = getYear(rows[info.row.index].reportDate) % 2 === 1;
            const reportDate = _reportDate
              ? lightFormat(
                  parseISO(_reportDate),
                  // eslint-disable-next-line no-nested-ternary
                  isPTTM ? `MM/yy 'PTTM'` : isTTM ? `MM/yy 'TTM'` : "MM/yyyy",
                )
              : "-";

            return (
              <div
                className={classNames(
                  "select-none",
                  "cursor-pointer whitespace-nowrap",
                  "bg-slate-50 text-slate-600",
                  "px-2 py-1.5 text-sm font-semibold text-slate-600",
                  "group-hover:bg-violet-50 group-hover:text-violet-700",
                  {
                    "bg-orange-400/10 text-orange-700": isTTM,
                    "bg-emerald-400/20 text-emerald-700": isPTTM,
                    "bg-sky-400/10 text-sky-700": isOdd && statementType === IFinancialsStatementType.Quarterly,
                  },
                  "dark:bg-slate-800 dark:text-slate-400 dark:group-hover:bg-slate-900 dark:group-hover:text-white",
                )}
              >
                <span>{reportDate}</span>
              </div>
            );
          },
        }),
        ...adversary.indicators.map(({ objectId, series }) => {
          const indicator = indicators.find((indicator) => indicator.objectId === objectId);
          invariant(indicator, `Indicator not found: "%s"`, objectId);

          const documentation =
            indicator?.documentation.find(({ languageCode }) => languageCode === localeLanguageCode) ||
            indicator?.preferredDocumentation;

          return columnHelper.accessor(
            (row) => {
              return series[row.index] ? series[row.index] : null;
            },
            {
              id: [adversary.tickerSymbol, objectId].join("/"),
              meta: {
                indicator,
              } as any,
              header: () => {
                const { backgroundColor } = indicator.columnTheme;
                const color =
                  theme === "dark"
                    ? tinycolor(indicator.columnTheme.backgroundColor).darken(25).toRgbString()
                    : indicator.columnTheme.textColor;

                return (
                  <IndicatorDocumentationPopover indicator={indicator}>
                    <PopoverTrigger asChild={true}>
                      <div
                        className={classNames(
                          "overflow-hidden text-ellipsis px-4 py-1.5 text-sm font-semibold text-slate-600",
                          "hover:cursor-pointer hover:underline",
                        )}
                        style={{
                          backgroundColor: theme === "dark" ? colors.slate[800] : backgroundColor,
                          color,
                        }}
                      >
                        {documentation.abbreviation || documentation.name}
                      </div>
                    </PopoverTrigger>
                  </IndicatorDocumentationPopover>
                );
              },
              cell: (info) => {
                const resolvedValue = info.getValue();

                const isDarkMode = theme === "dark";
                const cellHasTheme = !!resolvedValue?.theme;
                const isWhiteBackgroundColor = resolvedValue?.theme?.backgroundColor === "#ffffff";
                const isBlackTextColor = resolvedValue?.theme?.textColor === "#000000";
                const isDarkTextColor = tinycolor(resolvedValue?.theme?.textColor).isDark();
                const isSkeleton = resolvedValue?.__typename === "FinancialIndicatorSeriesSkeletonNode";
                let displayValue = resolvedValue ? resolvedValue.formattedValue : "-";
                if (displayValue === "L") {
                  // NOTE(Andrey): Remover `description` de todas as mensagens de "P"
                  displayValue = intl.formatMessage({
                    defaultMessage: "P",
                    description: "Abreviação de Prejuízo (Loss) no gráfico de cotação",
                    id: "/ywfi1",
                  });
                }

                return (
                  <div
                    className={classNames(
                      "whitespace-nowrap",
                      "px-4 py-1.5 text-sm font-normal",
                      "text-slate-600 dark:text-slate-400",
                      "group-data-[selected=false]:text-slate-600",
                      "dark:group-data-[selected=false]:text-slate-400",
                      "group-data-[selected=true]:!bg-transparent group-data-[selected=true]:!text-inherit",
                      {
                        "font-semibold": resolvedValue?.theme?.formatting.includes("BOLD"),
                        italic: resolvedValue?.theme?.formatting.includes("ITALIC"),
                      },
                    )}
                    style={{
                      backgroundColor: (() => {
                        if (isWhiteBackgroundColor) {
                          return undefined;
                        }

                        if (isDarkMode) {
                          return tinycolor(resolvedValue?.theme?.backgroundColor)
                            .darken(15)
                            .setAlpha(0.05)
                            .toRgbString();
                        }

                        return resolvedValue?.theme?.backgroundColor;
                      })(),
                      color: (() => {
                        // Dark mode modifiers
                        if (!isDarkMode) {
                          return resolvedValue?.theme?.textColor;
                        }

                        if (!cellHasTheme) {
                          return undefined;
                        }

                        if (!isDarkTextColor) {
                          return tinycolor(resolvedValue?.theme?.textColor).brighten(5).toRgbString();
                        }
                        if (isBlackTextColor) {
                          return tinycolor(resolvedValue?.theme?.textColor).brighten(70).toRgbString();
                        }
                        return tinycolor(resolvedValue?.theme?.textColor).brighten(30).toRgbString();
                      })(),
                    }}
                  >
                    <span
                      className={classNames({
                        "animate-pulse rounded-full bg-slate-200 text-transparent dark:bg-slate-700": loading,
                        "rounded-full bg-slate-200 text-transparent dark:bg-slate-700": isSkeleton,
                      })}
                    >
                      {displayValue}
                    </span>
                  </div>
                );
              },
            },
          );
        }),
      ],
    };
  });
}

function FinancialsComparisonTable({
  loading,
  statementType,
  indicators,
  adversaries,
  selectedIssuers,
  onChangeSelectedIssuers,
  rows,
}: FinancialsComparisonTableProps) {
  const columns = useComparisonColumns({
    loading,
    statementType,
    indicators,
    adversaries,
    selectedIssuers,
    onChangeSelectedIssuers,
    rows,
  });

  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (adversaries.length === 0) {
    return null;
  }

  return (
    <ScrollArea type="always" orientation="horizontal" className="relative">
      <div className="pb-4 lg:w-full">
        <div
          className={classNames("relative overflow-auto rounded-xl border border-slate-200", "dark:border-slate-950")}
        >
          <table className="w-full divide-y divide-slate-200 dark:divide-slate-700">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr
                  key={headerGroup.id}
                  className={classNames(
                    "select-none divide-slate-200",
                    "dark:divide-slate-700",
                    "[&>th]:whitespace-nowrap",
                    { "divide-x": headerGroup.depth >= 1 },
                    { "divide-x border-b border-b-gray-200 dark:border-b-slate-950": headerGroup.depth === 0 },
                  )}
                >
                  {headerGroup.headers.map((header) => {
                    return (
                      <th
                        key={header.id}
                        colSpan={header.colSpan}
                        className={classNames("m-0 p-0 text-center", header.column.columnDef.meta?.header?.className)}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>

            <tbody className="divide-y divide-slate-200 font-mono dark:divide-slate-700">
              {table.getRowModel().rows.map((row) => {
                return (
                  <tr
                    key={row.id}
                    className="group select-none divide-x divide-slate-200 hover:bg-teal-600/10 dark:divide-slate-700"
                  >
                    {row.getVisibleCells().map((cell) => {
                      return (
                        <td
                          key={cell.id}
                          className={classNames("m-0 p-0 text-center", cell.column.columnDef.meta?.cell?.className)}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </ScrollArea>
  );
}
